import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  VStack,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import CommonInputField from "@root/components/common/form/CommonInputField";
import UploadIcon from "@root/assets/icons/UploadIcon2.svg";
import { ACCEPTED_TYPES, MAX_FILE_SIZE_MB } from "@root/constants";

// Define proper form values type
type AddResourceFormValues = {
  title: string;
  description: string;
  file: File | null;
};

function AddResourceModal({
  isOpen,
  onClose,
  onSubmit,
  addResourcesLoading,
}: {
  onSubmit: (data: any) => void;
  isOpen: boolean;
  onClose: () => void;
  addResourcesLoading: boolean;
}) {
  const { t } = useTranslation("common");

  const methods = useForm<AddResourceFormValues>({
    mode: "onTouched",
    defaultValues: {
      title: "",
      description: "",
      file: null,
    },
  });

  const { control, handleSubmit, setValue, setError, clearErrors, reset } =
    methods;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Reset form & UI after successful upload
  useEffect(() => {
    if (!addResourcesLoading && isOpen) {
      reset(); // reset react-hook-form values
      setSelectedFile(null); // reset file UI
      setIsDragOver(false); // reset drag UI
    }
  }, [addResourcesLoading, isOpen, reset]);

  const handleFileSelect = (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("file", { message: t("faq.fileTypeError") });
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError("file", {
        message: t("faq.fileSizeError", { max: MAX_FILE_SIZE_MB }),
      });
      return;
    }
    clearErrors("file");
    setSelectedFile(file);
    setValue("file", file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p={2} bg="#fff" maxW={"700px"}>
        <ModalHeader fontWeight="semibold" fontSize="24px" color="#2F2F34">
          {t("faq.addResourceTitle")}
        </ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              <VStack spacing={5}>
                <CommonInputField
                  name="title"
                  control={control}
                  label={t("faq.titleLabel")}
                  placeholder={t("faq.titlePlaceholder")}
                  rules={{ required: t("faq.titleRequired") }}
                />
                <CommonInputField
                  name="description"
                  control={control}
                  label={t("faq.descriptionLabel")}
                  placeholder={t("faq.descriptionPlaceholder")}
                  rules={{ required: t("faq.descriptionRequired") }}
                />
                <Box
                  border={"1px solid #D0CDE1"}
                  padding={"5px"}
                  borderRadius={"16px"}
                  boxShadow={"0px 2.85px 8.56px 0px #67676933"}
                  width={"100%"}
                >
                  <Controller
                    name="file"
                    control={control}
                    rules={{
                      required: t("faq.fileRequired"),
                      validate: (file: File | null) => {
                        if (!file) return t("faq.fileRequired");
                        if (!ACCEPTED_TYPES.includes(file.type)) {
                          return t("faq.fileTypeError");
                        }
                        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                          return t("faq.fileSizeError", {
                            max: MAX_FILE_SIZE_MB,
                          });
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Box
                        border={`2px dashed ${
                          isDragOver ? "#1819B4" : "#B5C3CE"
                        }`}
                        borderRadius="16px"
                        p={6}
                        textAlign="center"
                        bg={isDragOver ? "#1819B40F" : "#FAFAFA"}
                        cursor="pointer"
                        transition="all 0.2s ease"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => {
                          document
                            .getElementById("resource-upload-input")
                            ?.click();
                        }}
                      >
                        <Input
                          type="file"
                          accept={ACCEPTED_TYPES.join(",")}
                          display="none"
                          id="resource-upload-input"
                          onChange={handleFileInputChange}
                        />

                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Image src={UploadIcon} width={120} />
                          <Text
                            color="#1819B4"
                            fontWeight={600}
                            fontSize="16px"
                          >
                            {isDragOver
                              ? t("faq.dropFileHere")
                              : t("faq.uploadAreaText")}{" "}
                            <span style={{ color: "#000" }}>
                              {t("faq.uploadAreaDragDrop")}
                            </span>
                          </Text>
                          <Text
                            color="#1819B4"
                            fontSize="14px"
                            mt={2}
                            bgColor={"#1A6C9C0F"}
                            p={"2px 12px"}
                            borderRadius={"24px"}
                          >
                            {t("faq.supportedFiles")}
                          </Text>

                          {selectedFile && (
                            <Text color="#1FC77E" fontSize="14px" mt={2}>
                              {selectedFile.name}
                            </Text>
                          )}

                          {fieldState.error && (
                            <Text color="red.500" fontSize="14px" mt={2}>
                              {fieldState.error.message}
                            </Text>
                          )}
                        </Box>
                      </Box>
                    )}
                  />
                </Box>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button
                variant="ghost"
                mr={3}
                fontSize="14px"
                fontWeight="medium"
                w="87px"
                h="44px"
                onClick={onClose}
              >
                {t("faq.cancelButton")}
              </Button>
              <Button
                type="submit"
                size="lg"
                alignSelf="flex-end"
                bgColor={"#1819B4"}
                fontSize={"14px"}
                fontWeight={500}
                isLoading={addResourcesLoading}
                loadingText={t("faq.UploadingResource")}
              >
                {addResourcesLoading ? <Spinner size="sm" mr={2} /> : null}
                {t("faq.uploadResource")}
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}

export default AddResourceModal;
